import { parseMoneyToCents, productSizes, withBoxFixedPrices, type SizedProduct } from '@agos/shared'

/**
 * The basket — the pure half, so the rules can be tested without a store.
 *
 * Two of C0's six findings live here and are fixed by construction rather than patched.
 *
 * **FB-C01 — one merge key, both ways in.** Legacy's `addItem` merges on product id alone while
 * `addItemWithExtras` merges on id *and* `pizzaExtras`. The topping modal calls both — the decline
 * button is `addItem` — so declining toppings on a pizza already in the basket increments the row that
 * has them, at the topping price. The customer is charged a surcharge for a pizza they pressed *no* on.
 * Here there is one {@link lineKey} and both paths use it.
 *
 * **FB-C02 — identity and quantity, never a price.** Legacy persists whole `Product` objects under
 * `fastkitchen-cart`, with no version and no expiry, and offers an `updateItemPrice` that writes into
 * the persisted copy. A basket left open in a tab keeps last month's price and the checkout has no
 * reason to notice. Here a line stores what was chosen; the price is read from the catalogue every time
 * and a line whose product has gone or changed is {@link reconcile}'s problem, not the order's.
 *
 * All money is integer cents (§14.7). `getSubtotal` in legacy is float euros, which is the arithmetic
 * that makes three items at 4,30 € come to 12,899999999999999.
 */

export interface CartLine {
  productId: number
  /** The toppings chosen, as the string the kitchen reads. Empty means none, and merges with none. */
  extras: string
  /**
   * The same toppings as ids — `FRONTEND.md` §9.168, priced by `BACKEND.md` §10A.80.
   *
   * `extras` above is what the kitchen ticket reads and what merges two lines. This is what the line
   * is **charged** from: the server prices each ingredient from `pizza_ingredients` at this line's
   * size, because §10A.9 re-reads every catalogue price and would otherwise discard a surcharge the
   * customer was quoted. Absent on every line built before this existed.
   */
  extrasChosen?: ReadonlyArray<{ ingredientId: number; quantity: number }>
  /**
   * What those toppings add, in cents — the figure the *screen* quotes.
   *
   * Computed by the client from the same `ingredientForSize` rule the server uses, so the two agree
   * to the cent. It is never sent: the server prices the ids and does not take this number.
   */
  extrasCents?: number
  /**
   * Which of the product's sizes — `FRONTEND.md` §9.85, priced by `BACKEND.md` §10A.33.
   *
   * The label as the menu spells it, `"50 cm"`. Absent for a product sold at one price, which is
   * every line built before this field existed.
   */
  size?: string
  /** Present only on a line that belongs to an assembled box. */
  boxGroupId?: string
  boxGroupName?: string
  boxSlotLabel?: string
  /** A daily-menu dish carries which meal it was, so a stale one can be recognised and dropped. */
  lunchMealType?: string
  /**
   * Set on a line the customer is being given free under a gift rule.
   *
   * The server decides whether it was really earned — `GiftRules.stripUnearnedGifts` drops the line if
   * the qualifying count is not met — so this is a claim, not an instruction.
   */
  giftRuleId?: number
  /**
   * A line the customer assembled, which no catalogue can price.
   *
   * A kombo pizza is three pizzas in one box charged as the dearest of them; it exists only because
   * somebody built it, so there is nothing to look it up in later. FB-C02's rule — identity and
   * quantity, never a price — is about not trusting a **stale catalogue price**, and a composed line has
   * no catalogue entry to go stale. The price is fixed at the moment it is composed, from the catalogue
   * as it was then, and the server is sent that figure with a negative `productId`, which is the
   * convention it already reads as *assembled, not picked*.
   */
  composed?: {
    name: string
    unitPriceCents: number
    /**
     * What it was assembled from — `BACKEND.md §10A.14`.
     *
     * Optional, and the server treats a line without it exactly as it did before. Sending it is what
     * lets `ServerPricing` cap the line at the sum of its parts, which is `BLOCKED.md` #12's other
     * half: a composed line's price travels with it, so without the parts there is nothing to check
     * it against.
     */
    parts?: { productId?: number; name: string; unitPriceCents: number; quantity: number }[]
    /**
     * What this composed line is — `BACKEND.md` §10A.26.
     *
     * `'kombo'` means *bound me by my dearest part, not the sum of them*, which is §10A.9's rule for
     * one. Absent means the sum, which is what every line sent until now and is why this is
     * additive.
     *
     * The server will not infer it: a kombo's parts each carry a catalogue id and a custom pizza's
     * carry none, so the two look distinguishable — but reading that as *price it max* would
     * under-charge a composed line that is honestly a sum of whole products.
     */
    composition?: 'kombo'
  }
  quantity: number
}

/**
 * What makes two lines the same line.
 *
 * The whole of FB-C01 is that legacy has two answers to this question. A box line is never merged at
 * all — two identical boxes are two boxes, and merging them would make the group unremovable as a unit.
 */
export function lineKey(
  line: Pick<CartLine, 'productId' | 'extras' | 'boxGroupId' | 'giftRuleId' | 'size'>,
): string {
  if (line.boxGroupId !== undefined) return `box:${line.boxGroupId}:${line.productId}`
  // A free one and a bought one are not the same line. Merging them would either give away the paid
  // item or charge for the gift, depending which line won — and legacy encodes the difference in a
  // negative product id precisely to keep them apart.
  if (line.giftRuleId !== undefined) return `gift:${line.giftRuleId}:${line.productId}`
  // §9.85 — the size is part of the identity. A 32 cm and a 50 cm Šunková are two lines at two
  // prices; merging them would charge one of the two wrongly, whichever won.
  return `p:${line.productId}:${line.extras}:${line.size ?? ''}`
}

export function addLine(lines: readonly CartLine[], line: CartLine): CartLine[] {
  const key = lineKey(line)
  const index = lines.findIndex((candidate) => lineKey(candidate) === key)
  if (index === -1) return [...lines, line]
  return lines.map((candidate, position) =>
    position === index ? { ...candidate, quantity: candidate.quantity + line.quantity } : candidate,
  )
}

export function changeQuantity(lines: readonly CartLine[], key: string, delta: number): CartLine[] {
  return lines
    .map((line) => (lineKey(line) === key ? { ...line, quantity: line.quantity + delta } : line))
    .filter((line) => line.quantity > 0)
}

export function removeLine(lines: readonly CartLine[], key: string): CartLine[] {
  return lines.filter((line) => lineKey(line) !== key)
}

/** A box leaves as a unit: removing one slot of it would leave a meal nobody ordered. */
export function removeBox(lines: readonly CartLine[], boxGroupId: string): CartLine[] {
  return lines.filter((line) => line.boxGroupId !== boxGroupId)
}

export function totalItems(lines: readonly CartLine[]): number {
  return lines.reduce((total, line) => total + line.quantity, 0)
}

// ─── pricing, always from the catalogue ──────────────────────────────────────

export interface PricedProduct {
  id: number
  name: string
  price: unknown
  active?: boolean
  /**
   * §9.401 - declared because `GET /products` answers it for every row and the daily menu now reads
   * a meal's popis from here. Pricing ignores it; a type that omits a field the data carries invites
   * a caller to pass the list somewhere it will read `undefined` and be believed.
   */
  description?: string | null
}

export interface PricedLine {
  line: CartLine
  key: string
  name: string
  unitCents: number
  totalCents: number
}

/**
 * The basket priced against the catalogue as it is now.
 *
 * A line whose product is gone or switched off gets no price and no total — it is reported by
 * {@link reconcile} and kept out of the sum, because charging for something the restaurant has withdrawn
 * is worse than an empty basket.
 *
 * A gift line is priced at zero here and sent at zero, but it still has to be *found* in the catalogue:
 * a gift whose product has been withdrawn is as gone as any other line, and showing it would promise
 * something the kitchen no longer makes.
 */
export function priceLines(
  lines: readonly CartLine[],
  products: readonly PricedProduct[],
  /**
   * A box's fixed price, by config id, in cents - §10A.107, the owner's item E.
   *
   * **Omitted means *price every box as the sum of its parts***, which is every box in the owner's
   * data today and what this function did before the parameter existed. Passed, it makes the screen
   * agree with what `ServerPricing` will bill: the server applies the same split from the same
   * configuration, and a client that showed the sum while the server charged the fixed price would be
   * §10A.80's defect again.
   */
  boxFixedPrices: ReadonlyMap<string, number> = new Map(),
): PricedLine[] {
  const priced = lines.flatMap((line) => {
    const product = products.find((candidate) => candidate.id === line.productId)
    if (product === undefined || product.active === false) return []
    // §9.85 — a line naming a size is priced at that size. `ServerPricing` does the same from the
    // same column (§10A.33), so the screen and the bill agree; a size the product no longer offers
    // falls back to its base price here and is refused by the server, which is the safer way round.
    const chosenSize =
      line.size === undefined
        ? undefined
        : productSizes(product as SizedProduct).find((size) => size.label === line.size)
    const base =
      line.giftRuleId !== undefined
        ? 0
        : chosenSize !== undefined
          ? chosenSize.priceCents
          : parseMoneyToCents(product.price as string | number | null)
    // §9.168 — the toppings, at the price the basket quoted when they were chosen. A gift stays at
    // nothing: a free pizza with ham on it is still free, which is what `giftRuleId` above decides.
    const unitCents = line.giftRuleId !== undefined ? 0 : base + (line.extrasCents ?? 0)
    return [
      {
        line,
        key: lineKey(line),
        name: line.size === undefined ? product.name : `${product.name} – ${line.size}`,
        unitCents,
        // A plain integer multiply: `unitCents` is already cents (FB-056).
        totalCents: unitCents * line.quantity,
      },
    ]
  })

  if (boxFixedPrices.size === 0) return priced

  // §10A.107 - a second pass, because a box is a *group* of lines and the map above sees one at a
  // time. `withBoxFixedPrices` is the same arithmetic the server runs, tested against the same
  // worked example on both sides.
  const shares = withBoxFixedPrices(
    priced.map((entry) => ({
      boxGroupId: entry.line.boxGroupId ?? null,
      unitCents: entry.unitCents,
      isGift: entry.line.giftRuleId !== undefined,
    })),
    boxFixedPrices,
  )
  return priced.map((entry, index) => {
    const unitCents = shares[index] ?? entry.unitCents
    if (unitCents === entry.unitCents) return entry
    return { ...entry, unitCents, totalCents: unitCents * entry.line.quantity }
  })
}

export function subtotalCents(priced: readonly PricedLine[]): number {
  return priced.reduce((total, line) => total + line.totalCents, 0)
}

export interface Reconciliation {
  /** Lines whose product the catalogue no longer offers. */
  missing: CartLine[]
  keep: CartLine[]
}

/**
 * What survived since the basket was filled.
 *
 * The answer to FB-C02: rather than trusting a persisted price, the basket is checked against the
 * catalogue on every load and anything that has gone is named. Legacy's equivalent is a hand-written
 * cleanup on `/` for one specific stale shape — *"items without it are from old code and must go"* —
 * which is a migration living in a page.
 */
export function reconcile(
  lines: readonly CartLine[],
  products: readonly PricedProduct[],
): Reconciliation {
  const known = new Set(
    products.filter((product) => product.active !== false).map((product) => product.id),
  )
  /**
   * §9.180 — a line the catalogue was never asked about is not a line the catalogue has withdrawn.
   *
   * A custom pizza, a kombo and a daily-menu meal all carry a **negative** id by construction
   * (`composedLine`, `lunchMealToCartId`), and a gift carries a synthetic one. None can ever be found
   * in `/products`, so every one of them was reported as gone — and `priceLines` drops what this
   * calls missing, so the warning sat beside a subtotal that had already stopped counting them.
   *
   * They are known by other means: a composed line carries its own price and parts, and a lunch meal
   * is priced from the day's menu, which is a second catalogue this function has never seen. The
   * check stays exactly as strict for the catalogue lines it was written for.
   */
  const isCatalogueLine = (line: CartLine) => line.productId > 0
  const survives = (line: CartLine) => !isCatalogueLine(line) || known.has(line.productId)
  return {
    missing: lines.filter((line) => !survives(line)),
    keep: lines.filter(survives),
  }
}

/**
 * A line the customer assembled — a kombo pizza today.
 *
 * The id is derived from what it is made of, so two identical kombos merge into one line of two and two
 * different ones stay apart. Legacy uses `-(Date.now() % 1000000)`, which makes **every** kombo a new
 * line: order the same three pizzas twice and the basket shows two lines of one, with a stepper on each
 * that steps a thing nobody can tell from its neighbour.
 *
 * Negative, because that is what the server reads as *assembled, not picked* — `OrderCreationController`
 * documents it, and the till and the kitchen board already follow it.
 */
export function composedLine(
  name: string,
  unitPriceCents: number,
  parts?: { productId?: number; name: string; unitPriceCents: number; quantity: number }[],
  composition?: 'kombo',
): CartLine {
  let hash = 5381
  for (let index = 0; index < name.length; index += 1) {
    hash = (Math.imul(hash, 33) ^ name.charCodeAt(index)) | 0
  }
  return {
    productId: hash >= 0 ? -(hash + 1) : hash,
    extras: '',
    quantity: 1,
    composed: {
      name,
      unitPriceCents,
      ...(parts && parts.length > 0 ? { parts } : {}),
      ...(composition === undefined ? {} : { composition }),
    },
  }
}

/** What the assembled lines in a basket come to, in cents. */
export function composedSubtotalCents(lines: readonly CartLine[]): number {
  return lines.reduce(
    (total, line) => total + (line.composed === undefined ? 0 : line.composed.unitPriceCents * line.quantity),
    0,
  )
}

/** One refundable charge attached to a product — `surcharge_definitions`, as `/products` sends it. */
export interface ProductDeposit {
  name?: string
  amount?: unknown
}

/**
 * What the deposits on this basket come to — FRONTEND.md §9.182.
 *
 * **The server charges these and the checkout was passing `depositCents: 0`.** `OrderCreationService`
 * adds `surchargeTotal` to the order total, so a basket with a deposit in it was quoted 2,15 € and
 * charged 2,30 €.
 *
 * **Per unit, not per line.** `surchargeTotal` multiplies by `quantity`, and three cans is three
 * deposits. Getting this wrong disagrees with the bill in the other direction, which is no better.
 *
 * **A gift carries none.** The server skips `productId <= 0` and a gift is priced at zero; charging a
 * deposit on something given away would be a third way to disagree.
 */
export function depositCentsOf(
  priced: readonly PricedLine[],
  products: readonly PricedProduct[],
): number {
  let total = 0
  for (const entry of priced) {
    if (entry.line.giftRuleId !== undefined || entry.line.productId <= 0) continue
    const product = products.find((candidate) => candidate.id === entry.line.productId) as
      | { surcharges?: ProductDeposit[] }
      | undefined
    for (const deposit of product?.surcharges ?? []) {
      total += parseMoneyToCents(deposit.amount as string | number | null) * entry.line.quantity
    }
  }
  return total
}
